import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Send, Loader2, MessageCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { UnifiedPdfCitationViewer } from './UnifiedPdfCitationViewer';
import { Customer } from './CustomerSelectionView';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

interface ConversationSummary {
  id: string;
  title: string | null;
  lastMessageAt: string | null;
  messageCount: number;
  createdAt: string;
}

interface IndexingStatus {
  documentsTotal: number;
  documentsIndexed: number;
  indexing: boolean;
}

interface DrawerMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  isStreaming?: boolean;
  systemStatus?: string;
}

interface SelectedCitation {
  id: string;
  page: number;
  text: string;
  startOffset: number;
  endOffset: number;
  documentId: string;
  documentName: string;
}

interface CustomerChatDrawerProps {
  customer: Customer | null;
  onClose: () => void;
}

/** Turns `[CITE: "exact text"]` markers into markdown links (`citation:N`) so
 * ReactMarkdown renders them inline; the custom `a` component below turns
 * those links into clickable citation chips instead of navigating. */
function preprocessCitations(text: string): { text: string; citations: string[] } {
  const citations: string[] = [];
  const processed = text.replace(/\[CITE:\s*"([^"]+)"\]/g, (_match, quote: string) => {
    const index = citations.length;
    citations.push(quote);
    const label = quote.length > 70 ? `${quote.slice(0, 67)}...` : quote;
    const safeLabel = label.replace(/[[\]]/g, '');
    return `[${safeLabel}](citation:${index})`;
  });
  return { text: processed, citations };
}

const CustomerChatDrawer: React.FC<CustomerChatDrawerProps> = ({ customer, onClose }) => {
  const { token } = useAuth();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DrawerMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<IndexingStatus | null>(null);
  const [customerDocuments, setCustomerDocuments] = useState<Array<{ id: string; name: string }>>([]);

  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<SelectedCitation | null>(null);

  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const authHeaders = useCallback(
    (): Record<string, string> => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const loadConversations = useCallback(async () => {
    if (!customer) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/customer-chat/${customer.id}/conversations`, {
        headers: authHeaders(),
      });
      if (res.ok) setConversations(await res.json());
    } catch (error) {
      console.error('Failed to load customer chat conversations:', error);
    }
  }, [customer, authHeaders]);

  const loadStatus = useCallback(async () => {
    if (!customer) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/customer-chat/${customer.id}/status`, {
        headers: authHeaders(),
      });
      if (res.ok) setStatus(await res.json());
    } catch (error) {
      console.error('Failed to load customer chat status:', error);
    }
  }, [customer, authHeaders]);

  const loadDocuments = useCallback(async () => {
    if (!customer) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/customers/${customer.id}/documents`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const docs = await res.json();
        setCustomerDocuments((Array.isArray(docs) ? docs : []).map((d: any) => ({ id: d.id, name: d.name })));
      }
    } catch (error) {
      console.error('Failed to load customer documents:', error);
    }
  }, [customer, authHeaders]);

  const loadConversation = useCallback(
    async (conversationId: string) => {
      try {
        const res = await fetchWithActivity(`${API_BASE_URL}/api/customer-chat/conversations/${conversationId}`, {
          headers: authHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        setActiveConversationId(data.id);
        setMessages(data.messages.map((m: any) => ({ id: m.id, content: m.content, sender: m.sender })));
      } catch (error) {
        console.error('Failed to load conversation:', error);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    if (!customer) return;
    setActiveConversationId(null);
    setMessages([]);
    setInputValue('');
    loadConversations();
    loadStatus();
    loadDocuments();
  }, [customer, loadConversations, loadStatus, loadDocuments]);

  useEffect(() => {
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }
    if (customer && status?.indexing) {
      statusPollRef.current = setInterval(loadStatus, 5000);
    }
    return () => {
      if (statusPollRef.current) clearInterval(statusPollRef.current);
    };
  }, [customer, status?.indexing, loadStatus]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
  }, [inputValue]);

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setInputValue('');
  };

  const handleSendMessage = async (message: string) => {
    if (!customer || isLoading || !message.trim()) return;

    const userMessage: DrawerMessage = { id: `user-${Date.now()}`, content: message, sender: 'user' };
    const aiMessageId = `ai-${Date.now()}`;
    const aiMessage: DrawerMessage = { id: aiMessageId, content: '', sender: 'ai', isStreaming: true };
    setMessages((prev) => [...prev, userMessage, aiMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetchWithActivity(`${API_BASE_URL}/api/customer-chat/${customer.id}/stream`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ conversationId: activeConversationId, message }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Chat failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'chunk') {
                fullResponse += data.content;
                setMessages((prev) => prev.map((m) => (m.id === aiMessageId ? { ...m, content: fullResponse } : m)));
              } else if (data.type === 'status') {
                setMessages((prev) => prev.map((m) => (m.id === aiMessageId ? { ...m, systemStatus: data.message } : m)));
              } else if (data.type === 'done') {
                if (!activeConversationId && data.conversationId) setActiveConversationId(data.conversationId);
                setMessages((prev) => prev.map((m) => (m.id === aiMessageId ? { ...m, isStreaming: false } : m)));
              } else if (data.type === 'title') {
                loadConversations();
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Streaming error');
              }
            } catch (parseError) {
              console.warn('Failed to parse customer chat SSE line:', line);
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      loadConversations();
    } catch (error) {
      console.error('Customer chat send failed:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === aiMessageId
            ? { ...m, content: 'Sorry, something went wrong sending that message. Please try again.', isStreaming: false }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCitationClick = async (quote: string) => {
    if (!customerDocuments.length) return;
    try {
      // A [CITE: "..."] marker doesn't carry which document it came from, so
      // search all of this customer's documents in parallel and take the
      // first hit — customers rarely hold enough documents for this to be costly.
      const attempts = await Promise.all(
        customerDocuments.map(async (doc) => {
          try {
            const res = await fetchWithActivity(`${API_BASE_URL}/api/chat/citations/find-quote`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ documentId: doc.id, quote }),
            });
            if (!res.ok) return null;
            const result = await res.json();
            return result.found && result.pageNumber ? { doc, result } : null;
          } catch {
            return null;
          }
        })
      );
      const hit = attempts.find(Boolean);
      if (!hit) return;
      const { doc, result } = hit;
      setSelectedCitation({
        id: `cite-${Date.now()}`,
        page: result.pageNumber,
        text: result.foundText || quote,
        startOffset: result.startOffset || 0,
        endOffset: result.endOffset || (result.foundText || quote).length,
        documentId: doc.id,
        documentName: result.documentName || doc.name,
      });
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Customer chat citation click failed:', error);
    }
  };

  if (!customer) return null;

  const customerName = customer.lineType === 'personal' ? customer.name : customer.company || customer.name;

  const renderMessageContent = (content: string) => {
    const { text, citations } = preprocessCitations(content);
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
            if (href?.startsWith('citation:')) {
              const idx = Number(href.slice('citation:'.length));
              const quote = citations[idx];
              return (
                <button
                  type="button"
                  onClick={() => quote && handleCitationClick(quote)}
                  className="inline text-primary underline decoration-primary/40 hover:decoration-primary underline-offset-2"
                  title="View source in document"
                >
                  {children}
                </button>
              );
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-4 sm:inset-6 lg:inset-10 xl:inset-y-8 xl:inset-x-24 z-50 bg-white dark:bg-dark-surface rounded-xl shadow-2xl flex overflow-hidden">
        {/* Conversation list sidebar */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 dark:border-dark-border flex flex-col bg-gray-50 dark:bg-gray-900/40">
          <div className="p-3 border-b border-gray-200 dark:border-dark-border">
            <button
              onClick={handleNewConversation}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text hover:bg-white dark:hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" /> New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => loadConversation(c.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate transition-colors ${
                  activeConversationId === c.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={c.title || 'Untitled conversation'}
              >
                {c.title || 'New conversation'}
              </button>
            ))}
            {conversations.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-2">No conversations yet.</p>
            )}
          </div>
        </div>

        {/* Main chat column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-dark-border flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <MessageCircle className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="font-medium text-secondary dark:text-dark-text truncate">{customerName}</span>
              {status?.indexing && (
                <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Indexing {status.documentsTotal - status.documentsIndexed}/{status.documentsTotal}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-6 py-8">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-24">
                  <MessageCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                  <h3 className="text-lg font-medium text-secondary dark:text-dark-text">Ask anything about {customerName}</h3>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                    Answers are grounded in this customer's uploaded documents and prior conversations.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {messages.map((m) => (
                    <div key={m.id}>
                      {m.sender === 'user' ? (
                        <div className="flex justify-end">
                          <div className="max-w-[75%] bg-primary text-white rounded-2xl rounded-br-sm px-4 py-2.5 whitespace-pre-wrap break-words">
                            {m.content}
                          </div>
                        </div>
                      ) : (
                        <div className="text-secondary dark:text-dark-text">
                          {m.systemStatus && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mb-1.5 italic">{m.systemStatus}</p>
                          )}
                          {m.content ? (
                            <div className="prose prose-sm dark:prose-invert max-w-none">{renderMessageContent(m.content)}</div>
                          ) : m.isStreaming ? (
                            <div className="flex gap-1 py-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          {/* Input bar */}
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-dark-border px-6 py-4">
            <div className="max-w-3xl mx-auto flex items-end gap-2 bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-2.5">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(inputValue);
                  }
                }}
                placeholder={`Message about ${customerName}...`}
                rows={1}
                className="flex-1 bg-transparent resize-none outline-none text-sm text-secondary dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 max-h-[200px] py-1"
              />
              <button
                onClick={() => handleSendMessage(inputValue)}
                disabled={isLoading || !inputValue.trim()}
                className="flex-shrink-0 p-2 rounded-full bg-primary text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                title="Send"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPdfViewer && selectedCitation && (
        <UnifiedPdfCitationViewer
          documentId={selectedCitation.documentId}
          citation={{
            documentId: selectedCitation.documentId,
            documentName: selectedCitation.documentName,
            page: selectedCitation.page,
            startOffset: selectedCitation.startOffset,
            endOffset: selectedCitation.endOffset,
            text: selectedCitation.text,
            context: `Citation from ${selectedCitation.documentName}, page ${selectedCitation.page}`,
          }}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedCitation(null);
          }}
          useAnnotationViewer={true}
        />
      )}
    </>
  );
};

export default CustomerChatDrawer;
